'use client';

import { useEffect, useRef, useState } from 'react';
import { closeOverlay, openOverlay, setOverlayMountTrigger, toggleOverlay } from '@/app/components/overlays/core/overlayManager';
import type { OverlayRegistrationProps } from '@/app/components/overlays/core/types';
import { getOverlayRegistryKey } from '@/app/components/overlays/utils/getOverlayRegistryKey';

/**
 * Allowed overlay mount actions queued before the overlay is mounted.
 */
type OverlayMountAction = 'open' | 'close' | 'toggle' | null;

/**
 * Return type for `useOverlayMountControl()`.
 */
interface OverlayMountControlReturn {
  shouldRender: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Controls lazy-mounted overlay behavior. Registers a mount trigger and queues any open/close/toggle actions
 * until the overlay is ready. Prevents race conditions by locking updates until action completes.
 *
 * @param props Overlay registration options (overlayKey and optional instanceId)
 * @returns Mount state and overlay control methods
 */
export function useOverlayMountControl(props: OverlayRegistrationProps): OverlayMountControlReturn {
  const registryKey = getOverlayRegistryKey(props);
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  const [nextAction, setNextAction] = useState<OverlayMountAction>(null);
  const actionLockRef = useRef(false);

  // Register the mount trigger only once
  useEffect(() => {
    setOverlayMountTrigger(props, () => {
      if (!shouldRender) setShouldRender(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryKey]);
  useEffect(() => {
    if (!nextAction || !shouldRender || actionLockRef.current) return;
    actionLockRef.current = true;
    requestAnimationFrame(() => {
      switch (nextAction) {
        case 'open':
          openOverlay(props);
          break;
        case 'close':
          closeOverlay(props);
          break;
        case 'toggle':
          toggleOverlay(props);
          break;
      }
      setNextAction(null);
      actionLockRef.current = false;
    });
  }, [nextAction, shouldRender, props]);

  /**
   * Queue an overlay action. If not mounted, schedule mount + action. If mounted, schedule action directly.
   */
  const queueAction = (action: OverlayMountAction): void => {
    if (actionLockRef.current) return;
    if (!shouldRender) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setNextAction(action);
      });
    } else {
      setNextAction(action);
    }
  };
  return {
    shouldRender,
    open: () => queueAction('open'),
    close: () => queueAction('close'),
    toggle: () => queueAction('toggle')
  };
}